import React, {useContext, useEffect} from 'react';
import {InnloggingsskjemaState} from "reducers/app-reducer";
import {DispatchContext} from "App";
import {useQuery} from "utils/hooks";
import Vadis from "services/vadisKontrollrapportService";
import {useApptekster} from "../contexts/ApptekstContext";
import {SVVTextInput} from "@komponentkassen/svv-form";
import {SVVButton} from "@komponentkassen/svv-button";
import {SVVInformationMessage} from "@komponentkassen/svv-information-message";
import {SVVSpinner} from "@komponentkassen/svv-spinner";
import {Controller, useForm} from "react-hook-form";
import { RequestBodyParams } from '../types/generated';

export enum Feilmelding {
    Ingen,
    Valideringsfeil,
    AnnenFeil,
    UgyldigLogin
}

type Props = {
    skjemaState: InnloggingsskjemaState
}

function fjernMellomromFraTekst(text: string): string {
    return text.replace(/\s+/g, "");
}

interface LoginData {
    formKontrollId: string;
    formKundeId: string;
    dokumentNokkel: string;
}

function Innlogging(props: Props) {
    const t = useApptekster();
    const appDispatch = useContext(DispatchContext);
    const queryparametre = useQuery();

    const kontrollId = queryparametre.get("kontrollId");
    const kundeId = queryparametre.get("kundeId");

    const {control, formState: {errors}, handleSubmit} = useForm<LoginData>({
        defaultValues: {formKontrollId: kontrollId || "", formKundeId: kundeId || "", dokumentNokkel: ""},
        mode: "all"
    });


    useEffect(() => {
        appDispatch({type: "SETT_KONTROLL_ID", value: kontrollId || ""});
        appDispatch({type: "SETT_KUNDE_ID", value: kundeId || ""});
    }, [appDispatch, kontrollId, kundeId]);

    const handterInnsending = ({formKontrollId, formKundeId, dokumentNokkel}: LoginData) => {
        const harMangler =
            formKontrollId.length === 0 ||
            formKundeId.length === 0 ||
            dokumentNokkel.length === 0;

        if (harMangler) {
            appDispatch({type: "VIS_INNLOGGING_FEILMELDING", value: Feilmelding.Valideringsfeil});
        } else {
            appDispatch({type: "LOGG_INN"});

            const innloggingsparametre: RequestBodyParams = {
                kontrollId: formKontrollId, kundeId: formKundeId, dokumentNokkel, dokumentId: ""
            };

            Vadis.hentKontrollrapport(
                innloggingsparametre
            ).then((response) => {
                appDispatch({
                    type: "VIS_LASTET_KONTROLLRAPPORT",
                    value: response
                });
            }).catch((status) => {
                if (status === 400) {
                    appDispatch({type: "VIS_INNLOGGING_FEILMELDING", value: Feilmelding.UgyldigLogin});
                } else {
                    appDispatch({type: "VIS_INNLOGGING_FEILMELDING", value: Feilmelding.AnnenFeil});
                }
            });
        }
    }

    function visFeilmelding(feilmelding: Feilmelding) {
        switch (feilmelding) {
            case Feilmelding.Ingen:
                return null;
            case Feilmelding.Valideringsfeil:
                return <SVVInformationMessage type="error"><p>{t("vadis-kontrollrapport-skjema.feil.validering")}</p></SVVInformationMessage>;
            case Feilmelding.UgyldigLogin:
                return <SVVInformationMessage type="error"><p>{t("vadis-kontrollrapport-skjema.feil.ugyldig")}</p></SVVInformationMessage>;
            case Feilmelding.AnnenFeil:
                return <SVVInformationMessage type="error"><p>{t("vadis-kontrollrapport-skjema.feil.annet")}</p></SVVInformationMessage>;
        }
    }

    function visSkjema(skjemaState: InnloggingsskjemaState) {
        const {kontrollId, kundeId, dokumentNokkel, feilmelding} = skjemaState;

        return (
            <form onSubmit={handleSubmit(handterInnsending)} noValidate>

                {visFeilmelding(feilmelding)}

                <p>{t("vadis-kontrollrapport-skjema.beskrivelse")}</p>

                <Controller
                    name='formKontrollId'
                    control={control}
                    rules={{ required: t('vadis-kontrollrapport-skjema.kontroll-id.mangler') }}
                    defaultValue={kontrollId}
                    render={({ field: { onChange, ref, value } }) => (
                        <SVVTextInput
                            ref={ref}
                            id="formKontrollId"
                            errorMessage={errors.formKontrollId?.message ? errors.formKontrollId.message : ""}
                            label={t("vadis-kontrollrapport-skjema.kontroll-id")}
                            value={value}
                            onChange={e => {
                                onChange(e);
                                appDispatch({type: "SETT_KONTROLL_ID", value: fjernMellomromFraTekst(e.target.value)})
                            }}
                        />
                    )}
                />

                <Controller
                    name='formKundeId'
                    control={control}
                    rules={{ required: t('vadis-kontrollrapport-skjema.kunde-id.mangler') }}
                    defaultValue={kundeId}
                    render={({ field: { onChange, ref, value } }) => (
                        <SVVTextInput
                            ref={ref}
                            id="formKundeId"
                            errorMessage={errors.formKundeId?.message ? errors.formKundeId.message : ""}
                            label={t("vadis-kontrollrapport-skjema.kunde-id")}
                            value={value}
                            onChange={e => {
                                onChange(e);
                                appDispatch({type: "SETT_KUNDE_ID", value: fjernMellomromFraTekst(e.target.value)})
                            }}
                        />
                    )}
                />

                <Controller
                    name='dokumentNokkel'
                    control={control}
                    rules={{ required: t('vadis-kontrollrapport-skjema.sikkerhetsnokkel.mangler') }}
                    defaultValue={dokumentNokkel}
                    render={({ field: { onChange, ref, value } }) => (
                        <SVVTextInput
                            ref={ref}
                            id="dokumentNokkel"
                            errorMessage={errors.dokumentNokkel?.message ? errors.dokumentNokkel.message : ""}
                            label={t("vadis-kontrollrapport-skjema.sikkerhetsnokkel")}
                            value={value}
                            onChange={e => {
                                onChange(e);
                                appDispatch({type: "SETT_DOKUMENTNOKKEL", value: fjernMellomromFraTekst(e.target.value)})
                            }}
                        />
                    )}
                />
                <SVVButton type="submit">{t("vadis-kontrollrapport-skjema.logg-inn")}</SVVButton>

            </form>
        )
    }

    if (props.skjemaState.laster) {
        return <SVVSpinner size="medium" centered={true} inline={false}>{t("vadis-kontrollrapport-skjema.laster")}</SVVSpinner>;
    } else {
        return visSkjema(props.skjemaState);
    }
}

export default Innlogging;
